<p-toast></p-toast>

<app-page-shell>
  <app-page-hero
    eyebrow="Booking Intake"
    title="Create Booking Container Cases"
    subtitle="Create one shared logistics project, one lifecycle case per container, and editable equipment journey plans in a single intake.">
    <div actions>
      <p-button label="Back to Cases" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" (click)="cancel()"></p-button>
    </div>
  </app-page-hero>

  <form [formGroup]="form" class="case-intake-layout">
    <div class="case-intake-main">
      <app-page-form-section
        icon="pi pi-book"
        title="Booking & Project Context"
        subtitle="A single project is created or reused for this booking or bill of lading.">
        <div class="booking-rule-strip">
          <i class="pi pi-link"></i>
          <div>
            <strong>One booking reference, one logistics project</strong>
            <span>All container cases created below share the same commercial and project context.</span>
          </div>
        </div>

        <div class="p-fluid grid formgrid booking-grid">
          <div class="field col-12 party-field">
            <app-commercial-party-picker
              [owningCompanyId]="companyId"
              [value]="commercialPartyReference"
              [required]="true"
              label="Container Customer"
              (valueChange)="onCommercialPartyChange($event)">
            </app-commercial-party-picker>
          </div>

          <div class="field col-12 md:col-6">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-bookmark"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-autoComplete
                  inputId="bookingNumber"
                  formControlName="bookingNumber"
                  [suggestions]="referenceSuggestions.BOOKING"
                  optionLabel="value"
                  optionValue="value"
                  [dropdown]="true"
                  [forceSelection]="false"
                  [minLength]="0"
                  appendTo="body"
                  styleClass="w-full"
                  (completeMethod)="searchReferences('BOOKING', $event.query)"
                  (onSelect)="onReferenceSelected($event.value)">
                  <ng-template let-reference pTemplate="item">
                    <div class="reference-option">
                      <strong>{{ reference.value }}</strong>
                      <small>{{ reference.billOfLadingNumber || reference.houseBillNumber || 'Existing booking reference' }}</small>
                    </div>
                  </ng-template>
                </p-autoComplete>
                <label for="bookingNumber">Booking Number</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>

          <div class="field col-12 md:col-6">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-file"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-autoComplete
                  inputId="billOfLadingNumber"
                  formControlName="billOfLadingNumber"
                  [suggestions]="referenceSuggestions.BILL_OF_LADING"
                  optionLabel="value"
                  optionValue="value"
                  [dropdown]="true"
                  [forceSelection]="false"
                  [minLength]="0"
                  appendTo="body"
                  styleClass="w-full"
                  (completeMethod)="searchReferences('BILL_OF_LADING', $event.query)"
                  (onSelect)="onReferenceSelected($event.value)">
                  <ng-template let-reference pTemplate="item">
                    <div class="reference-option">
                      <strong>{{ reference.value }}</strong>
                      <small>{{ reference.bookingNumber || reference.houseBillNumber || 'Existing bill of lading' }}</small>
                    </div>
                  </ng-template>
                </p-autoComplete>
                <label for="billOfLadingNumber">Bill of Lading</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>

          @if (!referenceReady() && (form.get('bookingNumber')?.touched || form.get('billOfLadingNumber')?.touched)) {
            <small class="p-error field col-12 reference-error">Enter either a booking number or a bill of lading number.</small>
          }

          <div class="field col-12 md:col-6">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-copy"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-autoComplete
                  inputId="houseBillNumber"
                  formControlName="houseBillNumber"
                  [suggestions]="referenceSuggestions.HOUSE_BILL"
                  optionLabel="value"
                  optionValue="value"
                  [dropdown]="true"
                  [forceSelection]="false"
                  [minLength]="0"
                  appendTo="body"
                  styleClass="w-full"
                  (completeMethod)="searchReferences('HOUSE_BILL', $event.query)"
                  (onSelect)="onReferenceSelected($event.value)">
                  <ng-template let-reference pTemplate="item">
                    <div class="reference-option">
                      <strong>{{ reference.value }}</strong>
                      <small>{{ reference.billOfLadingNumber || reference.bookingNumber || 'Existing house bill' }}</small>
                    </div>
                  </ng-template>
                </p-autoComplete>
                <label for="houseBillNumber">House Bill</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>

          <div class="field col-12 md:col-6">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-verified"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-autoComplete
                  inputId="customsDeclarationNumber"
                  formControlName="customsDeclarationNumber"
                  [suggestions]="referenceSuggestions.CUSTOMS_DECLARATION"
                  optionLabel="value"
                  optionValue="value"
                  [dropdown]="true"
                  [forceSelection]="false"
                  [minLength]="0"
                  appendTo="body"
                  styleClass="w-full"
                  (completeMethod)="searchReferences('CUSTOMS_DECLARATION', $event.query)"
                  (onSelect)="onReferenceSelected($event.value)">
                  <ng-template let-reference pTemplate="item">
                    <div class="reference-option">
                      <strong>{{ reference.value }}</strong>
                      <small>{{ reference.billOfLadingNumber || reference.bookingNumber || 'Existing customs declaration' }}</small>
                    </div>
                  </ng-template>
                </p-autoComplete>
                <label for="customsDeclarationNumber">Customs Declaration</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
        </div>
      </app-page-form-section>

      <app-page-form-section
        icon="pi pi-box"
        title="Container Manifest"
        subtitle="Set the expected quantity now. Original container numbers remain optional and can be captured later.">
        <div class="manifest-controls">
          <div class="container-count-control">
            <label class="container-count-label" for="numberOfContainers">Number of Containers *</label>
            <p-inputNumber
              inputId="numberOfContainers"
              formControlName="numberOfContainers"
              [useGrouping]="false"
              [min]="1"
              [max]="maximumContainers"
              [showButtons]="true"
              buttonLayout="horizontal"
              incrementButtonIcon="pi pi-plus"
              decrementButtonIcon="pi pi-minus"
              incrementButtonStyleClass="container-step-button"
              decrementButtonStyleClass="container-step-button">
            </p-inputNumber>
          </div>

          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-tag"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select
                inputId="defaultIsoEquipmentType"
                [options]="equipmentTypeOptions"
                optionLabel="label"
                optionValue="value"
                formControlName="defaultIsoEquipmentType"
                [showClear]="true"
                appendTo="body"
                styleClass="w-full">
              </p-select>
              <label for="defaultIsoEquipmentType">Default Equipment Type</label>
            </p-iftaLabel>
          </p-inputGroup>
        </div>

        <div formArrayName="containers" class="container-manifest-table">
          <div class="manifest-header" aria-hidden="true">
            <span>Case</span>
            <span>Original Container Number</span>
            <span>Equipment</span>
            <span>Seal</span>
          </div>
          @for (container of containers.controls; track $index) {
            <div class="manifest-row" [formGroupName]="$index">
              <span class="manifest-sequence">{{ $index + 1 }}</span>
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-box"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input [id]="'containerNumber-' + $index" pInputText formControlName="containerNumber">
                  <label [for]="'containerNumber-' + $index">Container Number (Optional)</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-tag"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select
                    [inputId]="'containerType-' + $index"
                    [options]="equipmentTypeOptions"
                    optionLabel="label"
                    optionValue="value"
                    formControlName="isoEquipmentType"
                    placeholder="Use default"
                    [showClear]="true"
                    appendTo="body"
                    styleClass="w-full">
                  </p-select>
                  <label [for]="'containerType-' + $index">Equipment Override</label>
                </p-iftaLabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-lock"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input [id]="'sealNumber-' + $index" pInputText formControlName="sealNumber">
                  <label [for]="'sealNumber-' + $index">Seal Number</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
          }
        </div>
      </app-page-form-section>

      <app-page-form-section
        icon="pi pi-directions"
        title="Equipment Journey Profile"
        subtitle="Start with a tenant profile, then tailor the transport and storage sequence before creating any cases.">
        <div class="journey-profile-toolbar">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-sitemap"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select
                inputId="equipmentJourneyProfileId"
                [options]="journeyProfiles"
                optionLabel="template.templateName"
                optionValue="template.id"
                formControlName="equipmentJourneyProfileId"
                [filter]="true"
                filterBy="template.templateName,template.templateCode"
                appendTo="body"
                styleClass="w-full"
                (onChange)="onProfileChange($event.value)">
                <ng-template let-profile pTemplate="item">
                  <div class="profile-option">
                    <strong>{{ profile.template?.templateName }}</strong>
                    <small>{{ profile.template?.templateCode }} · {{ profile.items?.length || 0 }} steps</small>
                  </div>
                </ng-template>
              </p-select>
              <label for="equipmentJourneyProfileId">Equipment Journey Profile *</label>
            </p-iftaLabel>
          </p-inputGroup>

          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-datePicker
                inputId="plannedJourneyStartAt"
                formControlName="plannedJourneyStartAt"
                [showTime]="true"
                hourFormat="12"
                appendTo="body"
                styleClass="w-full">
              </p-datePicker>
              <label for="plannedJourneyStartAt">Planned Journey Start *</label>
            </p-iftaLabel>
          </p-inputGroup>
        </div>

        @if (selectedProfile()) {
          <div class="selected-profile-summary">
            <div>
              <span>Selected Profile</span>
              <strong>{{ selectedProfile()?.template?.templateName }}</strong>
              <small>{{ selectedProfile()?.template?.description }}</small>
            </div>
            <p-tag value="Editable Blueprint" severity="info"></p-tag>
          </div>
        }

        <div class="journey-step-toolbar">
          <div>
            <strong>Journey Blueprint</strong>
            <span>{{ journeyBlueprint.length }} ordered steps · {{ unresolvedStepCount() }} require operational setup</span>
          </div>
          <div class="journey-step-actions">
            <p-button label="Transport Step" icon="pi pi-truck" severity="secondary" [outlined]="true" (click)="openStepDialog('TRANSPORT')"></p-button>
            <p-button label="Storage Step" icon="pi pi-warehouse" severity="secondary" [outlined]="true" (click)="openStepDialog('STORAGE')"></p-button>
          </div>
        </div>

        @if (journeyBlueprint.length) {
          <div class="journey-blueprint">
            @for (step of journeyBlueprint; track $index) {
              <article class="journey-step" [class.storage-step]="step.itemType === 'STORAGE'">
                <div class="journey-step-index">
                  <span>{{ $index + 1 }}</span>
                  <i class="pi" [ngClass]="step.itemType === 'TRANSPORT' ? 'pi-truck' : 'pi-warehouse'"></i>
                </div>
                <div class="journey-step-body">
                  <div class="journey-step-heading">
                    <div>
                      <small>{{ step.itemType === 'TRANSPORT' ? 'Transport' : 'Storage / Dwell' }}</small>
                      <strong>{{ step.stepName }}</strong>
                    </div>
                    <div class="compact-icon-actions">
                      <button type="button" class="icon-command" title="Edit step" (click)="openStepDialog(step.itemType || 'TRANSPORT', $index)">
                        <i class="pi pi-pencil"></i>
                      </button>
                      <button type="button" class="icon-command danger" title="Delete step" (click)="deleteStep($index)">
                        <i class="pi pi-trash"></i>
                      </button>
                    </div>
                  </div>
                  <div class="journey-step-location"><i class="pi pi-map-marker"></i>{{ stepLocation(step) }}</div>
                  <div class="journey-step-facts">
                    <span><b>Timing</b> +{{ step.startOffsetHours || 0 }}h for {{ step.plannedDurationHours || 0 }}h</span>
                    <span><b>Control</b> {{ stepControl(step) }}</span>
                    <span [class.needs-setup]="step.itemType === 'TRANSPORT' ? !step.originLocationId || !step.destinationLocationId : !step.facilityId">
                      <b>Readiness</b>
                      {{ (step.itemType === 'TRANSPORT' ? step.originLocationId && step.destinationLocationId : step.facilityId) ? 'Configured' : 'Assign before confirmation' }}
                    </span>
                  </div>
                </div>
              </article>
            }
          </div>
        } @else {
          <div class="journey-empty">
            <i class="pi pi-sitemap"></i>
            <strong>Select an equipment journey profile</strong>
            <span>The profile becomes an editable blueprint shared by every case in this intake.</span>
          </div>
        }

        <div class="journey-notes">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <textarea id="journeyNotes" pTextarea formControlName="journeyNotes" rows="3"></textarea>
              <label for="journeyNotes">Journey Planning Notes</label>
            </p-iftaLabel>
          </p-inputGroup>
        </div>
      </app-page-form-section>
    </div>

    <aside class="case-readiness-aside">
      <app-page-data-panel title="Booking Intake" subtitle="Review the shared project, case volume, and journey blueprint before creation.">
        <div class="readiness-panel">
          <div class="readiness-score">
            <span>Readiness</span>
            <strong>{{ readinessScore() }}%</strong>
          </div>
          <p-progressBar [value]="readinessScore()" [showValue]="false"></p-progressBar>

          <div class="readiness-status-list">
            <div [class.ready]="commercialReady()">
              <i class="pi" [ngClass]="commercialReady() ? 'pi-check-circle' : 'pi-circle'"></i>
              <span>Commercial party</span>
            </div>
            <div [class.ready]="referenceReady()">
              <i class="pi" [ngClass]="referenceReady() ? 'pi-check-circle' : 'pi-circle'"></i>
              <span>Booking or BL reference</span>
            </div>
            <div [class.ready]="manifestReady()">
              <i class="pi" [ngClass]="manifestReady() ? 'pi-check-circle' : 'pi-circle'"></i>
              <span>Container manifest</span>
            </div>
            <div [class.ready]="profileReady()">
              <i class="pi" [ngClass]="profileReady() ? 'pi-check-circle' : 'pi-circle'"></i>
              <span>Equipment journey profile</span>
            </div>
          </div>

          <div class="booking-summary-card">
            <div><span>Logistics Project</span><strong>Created or reused automatically</strong></div>
            <div><span>Container Cases</span><strong>{{ form.get('numberOfContainers')?.value || 0 }}</strong></div>
            <div><span>Journey Steps / Case</span><strong>{{ journeyBlueprint.length }}</strong></div>
            <div><span>Unresolved Steps</span><strong>{{ unresolvedStepCount() }}</strong></div>
          </div>

          <div class="intake-warning">
            <i class="pi pi-info-circle"></i>
            <span>Container numbers may be blank at intake. Each row still creates its own traceable lifecycle case.</span>
          </div>

          <div class="editor-actions">
            <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (click)="cancel()"></p-button>
            <p-button
              [label]="'Create ' + (form.get('numberOfContainers')?.value || 0) + ' Cases'"
              icon="pi pi-check"
              (click)="submit()"
              [loading]="isSubmitting">
            </p-button>
          </div>
        </div>
      </app-page-data-panel>
    </aside>
  </form>
</app-page-shell>

<p-dialog
  [(visible)]="stepDialogVisible"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [closable]="true"
  [style]="{ width: 'min(980px, 94vw)' }"
  [breakpoints]="{ '860px': '96vw' }"
  styleClass="journey-step-dialog">
  <ng-template pTemplate="header">
    <div class="dialog-title-row">
      <span class="dialog-title-icon"><i class="pi" [ngClass]="stepForm.get('itemType')?.value === 'STORAGE' ? 'pi-warehouse' : 'pi-truck'"></i></span>
      <div>
        <strong>{{ editingStepIndex == null ? 'Add' : 'Edit' }} Journey Step</strong>
        <small>Tailor the booking blueprint before downstream operations are created.</small>
      </div>
    </div>
  </ng-template>

  <div class="master-dialog-body">
    <aside class="master-dialog-summary">
      <div class="summary-icon"><i class="pi pi-sitemap"></i></div>
      <div>
        <small>Equipment Journey</small>
        <h3>{{ stepForm.get('itemType')?.value === 'STORAGE' ? 'Storage / dwell step' : 'Transport demand step' }}</h3>
        <p>Changes apply to the new cases in this booking intake. The tenant profile remains unchanged.</p>
      </div>
      <div class="master-dialog-facts">
        <div><span>Position</span><strong>{{ (editingStepIndex ?? journeyBlueprint.length) + 1 }}</strong></div>
        <div><span>Control</span><strong>Draft until confirmed</strong></div>
      </div>
    </aside>

    <form [formGroup]="stepForm" class="master-dialog-form">
      <div class="dialog-form-section master-wide-field">
        <div class="dialog-section-label"><i class="pi pi-list"></i> Step Definition</div>
        <div class="dialog-fields two-column">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-directions"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select inputId="itemType" [options]="stepTypeOptions" optionLabel="label" optionValue="value" formControlName="itemType" appendTo="body" styleClass="w-full"></p-select>
              <label for="itemType">Step Type *</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-pencil"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input id="stepName" pInputText formControlName="stepName">
              <label for="stepName">Step Name *</label>
            </p-iftaLabel>
          </p-inputGroup>
        </div>
      </div>

      @if (stepForm.get('itemType')?.value === 'TRANSPORT') {
        <div class="dialog-form-section master-wide-field">
          <div class="dialog-section-label"><i class="pi pi-truck"></i> Transport Demand</div>
          <div class="dialog-fields two-column">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-compass"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="transportMode" [options]="transportModeOptions" optionLabel="label" optionValue="value" formControlName="transportMode" appendTo="body" styleClass="w-full"></p-select>
                <label for="transportMode">Mode</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-flag"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="priority" [options]="priorityOptions" optionLabel="label" optionValue="value" formControlName="priority" appendTo="body" styleClass="w-full"></p-select>
                <label for="priority">Priority</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="originLocationId" appLazyLocationPicker optionLabel="name" optionValue="id" formControlName="originLocationId" [filter]="true" filterBy="name" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                <label for="originLocationId">Origin Location</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="destinationLocationId" appLazyLocationPicker optionLabel="name" optionValue="id" formControlName="destinationLocationId" [filter]="true" filterBy="name" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                <label for="destinationLocationId">Destination Location</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
        </div>
      } @else {
        <div class="dialog-form-section master-wide-field">
          <div class="dialog-section-label"><i class="pi pi-warehouse"></i> Storage Plan</div>
          <div class="dialog-fields two-column">
            <p-inputGroup class="wide-dialog-field">
              <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="facilityId" [options]="facilities" optionLabel="facilityName" optionValue="id" formControlName="facilityId" [filter]="true" filterBy="facilityName,facilityCode" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                <label for="facilityId">Terminal / Facility</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-inbox"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="storageReason" [options]="storageReasonOptions" optionLabel="label" optionValue="value" formControlName="storageReason" appendTo="body" styleClass="w-full"></p-select>
                <label for="storageReason">Storage Reason</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
        </div>
      }

      <div class="dialog-form-section master-wide-field">
        <div class="dialog-section-label"><i class="pi pi-clock"></i> Relative Plan Window</div>
        <div class="dialog-fields two-column">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-forward"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-inputNumber inputId="startOffsetHours" formControlName="startOffsetHours" [min]="0" suffix=" hours" [useGrouping]="false"></p-inputNumber>
              <label for="startOffsetHours">Starts After *</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-stopwatch"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-inputNumber inputId="plannedDurationHours" formControlName="plannedDurationHours" [min]="0" suffix=" hours" [useGrouping]="false"></p-inputNumber>
              <label for="plannedDurationHours">Planned Duration *</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup class="wide-dialog-field">
            <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <textarea id="stepRemarks" pTextarea formControlName="remarks" rows="3"></textarea>
              <label for="stepRemarks">Planning Notes</label>
            </p-iftaLabel>
          </p-inputGroup>
        </div>
      </div>
    </form>
  </div>

  <ng-template pTemplate="footer">
    <div class="dialog-footer-actions">
      <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (click)="stepDialogVisible = false"></p-button>
      <p-button label="Save Step" icon="pi pi-check" (click)="saveStep()"></p-button>
    </div>
  </ng-template>
</p-dialog>
